import { useState, useEffect, useRef } from 'react';
import './style.css';
import LottieAnimation from './components/LottieAnimation';
import { VAPI_CONFIG } from './config';
import Vapi from '@vapi-ai/web';
import { Routes, Route } from 'react-router-dom';
import { DashboardRoutes } from './pages/Dashboard';
import { AuthProvider } from './contexts/AuthContext';
import { PitchPage } from './pages/Pitch';
import { AlmoosaPage } from './pages/Almoosa';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';

const translations = {
  en: {
    getStarted: "Get Started",
    heroTitle: "Your AI Voice Agents",
    heroSubtitle: "Transform customer experiences with intelligent voice agents that sound natural",
    swipingWords: ["Bold", "Modern", "Revolutionary"],
    bookDemo: "Book a Demo",
    tryNow: "Let's Talk",
    listening: "Listening...",
    speaking: "Speaking...",
    naturalConversations: "Natural Conversations",
    naturalConversationsDesc: "Our agents handle complex dialogues with natural language understanding that feels natural",
    seamlessIntegration: "Seamless Integration",
    seamlessIntegrationDesc: "Connect to your existing systems in minutes, not months. API-first architecture for easy deployment",
    actionableInsights: "Actionable Insights",
    actionableInsightsDesc: "Gain valuable customer insights with real-time analytics and conversation intelligence",
    workflowTitle: "Build Powerful Voice Automation Workflows",
    triggerEvent: "Trigger Event",
    triggerEventDesc: "Customer call, form submission, or scheduled event activates your AI voice agent",
    voiceInteraction: "AI Voice Interaction",
    voiceInteractionDesc: "Our agent handles the conversation naturally, collects information, and makes decisions",
    integrationAction: "Integration & Action",
    integrationActionDesc: "Agent updates your CRM, schedules appointments, or triggers other automated processes",
    businessOutcome: "Business Outcome",
    businessOutcomeDesc: "Task completed with no human intervention, freeing your team for high-value work",
    addNode: "+ Add Node",
    deployWorkflow: "Deploy Workflow",
    testimonialsTitle: "What Our Clients Say",
    ctaTitle: "Ready to Transform Your Voice Experience?",
    ctaSubtitle: "Join industry leaders who are already leveraging VoiceAI to create exceptional customer experiences.",
    scheduleDemo: "Talk to our AI agent and explain your needs",
    copyright: "© 2025 VoiceAI. All rights reserved.",
    example1: "Schedule Appointment",
    example2: "Order Status",
    example3: "Support & FAQ",
    formTitle: "Tell us what you need",
    formSubtitle: "Describe your requirements as if you're talking to ChatGPT",
    mobileNumber: "Mobile Number",
    requirements: "Your need as if you're talking to ChatGPT",
    submit: "Submit",
    mobilePlaceholder: "05XXXXXXXX",
    requirementsPlaceholder: "Describe what you need...",
    successMessage: "Thanks, will be in touch soon",
    startNow: "Start Now",
    useCasesTitle: "Real-World Applications",
    retail: "Retail",
    retailFeatures: [
      "24/7 customer support",
      "Order processing & tracking",
      "Product recommendations",
      "Inventory inquiries"
    ],
    healthcare: "Healthcare",
    healthcareFeatures: [
      "Appointment scheduling",
      "Medication reminders",
      "Patient follow-ups",
      "Basic health inquiries"
    ],
    realEstate: "Real Estate",
    realEstateFeatures: [
      "Property inquiries",
      "Viewing scheduling",
      "Lead qualification",
      "Market updates"
    ],
    more: "& Much More",
    moreFeatures: [
      "Custom industry solutions",
      "Multi-language support",
      "Integration with existing systems",
      "Scalable automation"
    ],
    loadingAnimation: "Loading animation...",
    submitting: "Submitting...",
    formError: "Sorry, there was an error submitting your message. Please try again.",
    mobileNumberError: "Please enter a valid 10-digit mobile number"
  },
  ar: {
    getStarted: "ابدأ الآن",
    heroTitle: "وكلاء الذكاء الاصطناعي الصوتي",
    heroSubtitle: "حول تجارب العملاء باستخدام وكلاء صوت ذكيين يبدون طبيعيين",
    swipingWords: ["جريء", "حديث", "ثوري"],
    bookDemo: "احجز عرضاً",
    tryNow: "دعنا نتحدث",
    listening: "جاري الاستماع...",
    speaking: "يتحدث...",
    naturalConversations: "محادثات طبيعية",
    naturalConversationsDesc: "يتعامل وكلاؤنا مع الحوارات المعقدة بفهم طبيعي للغة يبدو طبيعياً",
    seamlessIntegration: "تكامل سلس",
    seamlessIntegrationDesc: "اتصل بأنظمتك الحالية في دقائق، وليس أشهر. هندسة API-first للنشر السهل",
    actionableInsights: "رؤى قابلة للتنفيذ",
    actionableInsightsDesc: "احصل على رؤى قيمة للعملاء مع تحليلات في الوقت الفعلي وذكاء المحادثة",
    workflowTitle: "بناء سير عمل أتمتة صوتية قوية",
    triggerEvent: "حدث الزناد",
    triggerEventDesc: "مكالمة العميل، تقديم النموذج، أو حدث مجدول ينشط وكيل الذكاء الاصطناعي الصوتي الخاص بك",
    voiceInteraction: "تفاعل صوتي بالذكاء الاصطناعي",
    voiceInteractionDesc: "يتعامل وكيلنا مع المحادثة بشكل طبيعي، ويجمع المعلومات، ويتخذ القرارات",
    integrationAction: "التكامل والعمل",
    integrationActionDesc: "يقوم الوكيل بتحديث CRM الخاص بك، وجدولة المواعيد، أو تشغيل عمليات آلية أخرى",
    businessOutcome: "نتيجة الأعمال",
    businessOutcomeDesc: "اكتملت المهمة دون تدخل بشري، مما يحرر فريقك للعمل ذو القيمة العالية",
    addNode: "+ إضافة عقدة",
    deployWorkflow: "نشر سير العمل",
    testimonialsTitle: "ماذا يقول عملاؤنا",
    ctaTitle: "هل أنت مستعد لتحويل تجربة الصوت الخاصة بك؟",
    ctaSubtitle: "انضم إلى قادة الصناعة الذين يستفيدون بالفعل من VoiceAI لإنشاء تجارب عملاء استثنائية.",
    scheduleDemo: "تحدث إلى وكيل الذكاء الاصطناعي واشرح احتياجاتك",
    copyright: "© 2025 VoiceAI. جميع الحقوق محفوظة.",
    example1: "حجز موعد",
    example2: "تتبع الطلب",
    example3: "الدعم والأسئلة الشائعة",
    formTitle: "أخبرنا بما تحتاج",
    formSubtitle: "اشرح متطلباتك كما لو كنت تتحدث مع ChatGPT",
    mobileNumber: "رقم الجوال",
    requirements: "احتياجك كما لو كنت تتحدث مع ChatGPT",
    submit: "إرسال",
    mobilePlaceholder: "05XXXXXXXX",
    requirementsPlaceholder: "اشرح ما تحتاجه...",
    successMessage: "شكراً، سنتواصل معك قريباً",
    startNow: "ابدأ الآن",
    useCasesTitle: "تطبيقات عملية",
    retail: "التجزئة",
    retailFeatures: [
      "دعم العملاء على مدار الساعة",
      "معالجة وتتبع الطلبات",
      "توصيات المنتجات",
      "استفسارات المخزون"
    ],
    healthcare: "الرعاية الصحية",
    healthcareFeatures: [
      "جدولة المواعيد",
      "تذكير بالأدوية",
      "متابعة المرضى",
      "استفسارات صحية أساسية"
    ],
    realEstate: "العقارات",
    realEstateFeatures: [
      "استفسارات العقارات",
      "جدولة المشاهدات",
      "تأهيل العملاء المحتملين",
      "تحديثات السوق"
    ],
    more: "والمزيد",
    moreFeatures: [
      "حلول صناعية مخصصة",
      "دعم متعدد اللغات",
      "التكامل مع الأنظمة الحالية",
      "أتمتة قابلة للتطوير"
    ],
    loadingAnimation: "جاري تحميل الرسوم المتحركة...",
    submitting: "جاري الإرسال...",
    formError: "عذراً، حدث خطأ أثناء إرسال رسالتك. يرجى المحاولة مرة أخرى.",
    mobileNumberError: "يرجى إدخال رقم جوال صحيح مكون من 10 أرقام"
  }
};

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <button 
      className="theme-toggle"
      onClick={toggleTheme}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
    >
      {theme === 'dark' ? '☀️' : '🌙'}
    </button>
  );
};

const SwipingText = ({ language }) => {
  const [currentWord, setCurrentWord] = useState(0);
  const words = translations[language].swipingWords;
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 2000);
    
    return () => clearInterval(interval);
  }, [words.length]);

  return (
    <div className="swiping-text">
      <span className="swiping-word">{words[currentWord]}</span>
    </div>
  );
};

const CustomForm = ({ language }) => {
  const [formData, setFormData] = useState({
    mobileNumber: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const t = translations[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formFields = {
        'entry.1487169208': formData.mobileNumber,
        'entry.432444317': formData.requirements
      };

      const formUrl = 'https://docs.google.com/forms/d/e/1FAIpQLSc7zcqaHTh0RwbYlpyPlrDUomtYG3zUu_wUeMQwJJPRGG2qGw/formResponse';
      
      const formDataToSubmit = new FormData();
      Object.entries(formFields).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      await fetch(formUrl, {
        method: 'POST',
        mode: 'no-cors',
        body: formDataToSubmit
      });

      setSubmitStatus('success');
      setFormData({ mobileNumber: '', requirements: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (submitStatus === 'success') {
    return (
      <div className="success-container">
        <div className="success-icon">✓</div>
        <p>{t.successMessage}</p>
      </div>
    );
  }

  return (
    <form className="custom-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="mobileNumber">{t.mobileNumber}</label>
        <input
          type="tel"
          id="mobileNumber"
          name="mobileNumber"
          value={formData.mobileNumber}
          onChange={handleChange}
          placeholder={t.mobilePlaceholder}
          required
          pattern="[0-9]{10}"
          title={t.mobileNumberError}
        />
      </div>
      <div className="form-group">
        <label htmlFor="requirements">{t.requirements}</label>
        <textarea
          id="requirements"
          name="requirements"
          value={formData.requirements}
          onChange={handleChange}
          placeholder={t.requirementsPlaceholder}
          required
        />
      </div>
      <button 
        type="submit" 
        className="submit-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? t.submitting : t.submit}
      </button>
      {submitStatus === 'error' && (
        <div className="form-message error">
          {t.formError}
        </div>
      )}
    </form>
  );
};

const LandingPage = () => {
  const [animationData, setAnimationData] = useState(null);
  const [, setIsVapiActive] = useState(false);
  const [, setIsSpeaking] = useState(false);
  const [, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setShowRating] = useState(false);
  const [, setRating] = useState(0);
  const [showThankYou, setShowThankYou] = useState(false);
  const [, setVapi] = useState(null);
  const vapiRef = useRef(null);
  const [language] = useState('en');
  const [, setUserName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [, setShowPhoneInput] = useState(false);
  const t = translations[language];

  useEffect(() => {
    // Load the Lottie animation data
    fetch('/animations/voice-ai.json')
      .then(response => response.json())
      .then(data => {
        console.log('Animation data loaded:', data); // Debug log
        setAnimationData(data);
      })
      .catch(error => {
        console.error('Error loading animation:', error);
        // Try loading from public directory as fallback
        fetch('/voice-ai.json')
          .then(response => response.json())
          .then(data => {
            console.log('Animation data loaded from fallback:', data);
            setAnimationData(data);
          })
          .catch(error => console.error('Error loading animation from fallback:', error));
      });

    // Initialize Vapi
    const initializeVapi = async () => {
      try {
        if (!VAPI_CONFIG || !VAPI_CONFIG.PUBLIC_KEY) {
          console.error('Vapi PUBLIC_KEY is not configured.');
          return;
        }
        const vapiInstance = new Vapi(VAPI_CONFIG.PUBLIC_KEY);
        vapiRef.current = vapiInstance;
        setVapi(vapiInstance);

        vapiInstance.on('call-start', () => {
          setIsVapiActive(true);
          setIsSpeaking(false);
          setIsListening(true);
          setIsLoading(false);
          setShowRating(false);
        });
        vapiInstance.on('call-end', () => {
          setIsVapiActive(false);
          setIsSpeaking(false);
          setIsListening(false);
          setIsLoading(false);
          setShowRating(true);
        });
        vapiInstance.on('error', (e) => {
          console.error('Vapi error:', e);
          setIsVapiActive(false);
          setIsSpeaking(false);
          setIsListening(false);
          setIsLoading(false);
        });
        vapiInstance.on('speech-start', () => {
          setIsSpeaking(true);
          setIsListening(false);
        });
        vapiInstance.on('speech-end', () => {
          setIsSpeaking(false);
          setIsListening(true);
        });

        vapiInstance.on('message', (message) => {
          if (message.toolCallList && message.toolCallList.length > 0) {
            const toolCall = message.toolCallList[0];
            if (toolCall.function?.name === 'capture_name') {
              const name = toolCall.function.arguments.name;
              setUserName(name);
            }
          }
        });

      } catch (error) {
        console.error('Failed to initialize Vapi:', error);
      }
    };
    initializeVapi();
    return () => vapiRef.current?.stop();
  }, []);

  // Format phone number to +966 format
  const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // Remove leading 0 if present
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    
    // Add +966 prefix
    return '+966' + cleaned;
  };

  const startVapiAssistant = async () => {
    // If no phone number, show phone input
    if (!phoneNumber) {
      setShowPhoneInput(true);
      return;
    }

    try {
      setIsLoading(true);
      setShowThankYou(false);
      setRating(0);

      // Format phone number to +966 format
      const formattedPhone = formatPhoneNumber(phoneNumber);

      // Call backend proxy (which calls Retool)
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/demo-call-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          phone: formattedPhone
        })
      });

      if (response.ok) {
        setShowPhoneInput(false);
        setShowThankYou(true);
        setPhoneNumber(''); // Clear phone number
      } else {
        throw new Error('Failed to start demo call');
      }
    } catch (error) {
      console.error('Failed to start demo call:', error);
      alert('Failed to start demo call. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="landing-page-container">
      <div className="container">
        <header>
          <div className="logo">Intuitive<span>AI</span></div>
          <div className="header-actions">
            <a href="#contact" className="start-now-button">
              {t.startNow}
            </a>
            {/* <button className="language-toggle" onClick={toggleLanguage} aria-label={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}>
              {language === 'en' ? '🇸🇦' : '🇺🇸'}
            </button> */}
            <ThemeToggle />
          </div>
        </header>
        
        <section className="hero">
          <div className="hero-content">
            <h1>
                {t.heroTitle}
                <SwipingText language={language} />
            </h1>
            <p className="tagline">{t.heroSubtitle}</p>
            <div className="hero-buttons">
                <a href="#contact" className="start-now-button">
                    {t.startNow}
                </a>
            </div>

            <div className="example-buttons">
              {showThankYou ? (
                <div className="success-container">
                  <div className="success-icon">📞</div>
                  <p>{language === 'ar' ? 'ستتلقى مكالمة خلال دقائق قليلة!' : 'You will receive a call shortly!'}</p>
                  <p className="success-subtitle">{language === 'ar' ? 'شكراً لك!' : 'Thank you!'}</p>
                </div>
              ) : (
                <div className="phone-input-container">
                  <div className="phone-input-message">
                    <p>{language === 'ar' ? 'أدخل رقم هاتفك للتحدث مع نورة' : 'Enter your phone number to speak with Noura'}</p>
                  </div>
                  <div className="phone-input-wrapper">
                    <input
                      type="tel"
                      placeholder="05xxxxxxxx"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="phone-input"
                      onKeyPress={(e) => e.key === 'Enter' && startVapiAssistant()}
                    />
                    <button
                      className="phone-submit-button"
                      onClick={startVapiAssistant}
                      disabled={!phoneNumber.trim() || isLoading}
                    >
                      {isLoading ? (
                        <span className="spinner"></span>
                      ) : (
                        language === 'ar' ? 'ابدأ المكالمة' : 'Start Call'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          

        </section>

        
        {/* <section className="features">
          <div className="feature">
            <div className="feature-icon">🎯</div>
            <h3>{t.naturalConversations}</h3>
            <p>{t.naturalConversationsDesc}</p>
          </div>
          
          <div className="feature">
            <div className="feature-icon">⚡</div>
            <h3>{t.seamlessIntegration}</h3>
            <p>{t.seamlessIntegrationDesc}</p>
          </div>
          
          <div className="feature">
            <div className="feature-icon">📊</div>
            <h3>{t.actionableInsights}</h3>
            <p>{t.actionableInsightsDesc}</p>
          </div>
        </section> */}

        <div className="animation-container">
          {animationData ? (
            <LottieAnimation
              animationData={animationData}
              className="bottom-animation"
              loop={true}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
              speed={0.5}
            />
          ) : (
            <div>{t.loadingAnimation}</div>
          )}
        </div>

        <section className="use-cases">
          <h2>{t.useCasesTitle}</h2>
          <div className="use-cases-grid">
            <div className="use-case-card">
              <div className="use-case-icon">🏪</div>
              <h3>{t.retail}</h3>
              <ul>
                {t.retailFeatures.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">🏥</div>
              <h3>{t.healthcare}</h3>
              <ul>
                {t.healthcareFeatures.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
            
            <div className="use-case-card">
              <div className="use-case-icon">🏢</div>
              <h3>{t.realEstate}</h3>
              <ul>
                {t.realEstateFeatures.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">✨</div>
              <h3>{t.more}</h3>
              <ul>
                {t.moreFeatures.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="contact" className="contact-form">
          <div className="form-content">
            <h2>{t.formTitle}</h2>
            <CustomForm language={language} />
          </div>
        </section>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pitch" element={<PitchPage />} />
          <Route path="/almoosa" element={<AlmoosaPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route 
            path="/dashboard/*" 
            element={
              <ProtectedRoute>
                <DashboardRoutes />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 